import { localDatabaseUrls } from "../backend/constants";
import { getJWTToken } from "../shared/fetchWrapper/fetchWrapper";
import { postAPI } from "../backend/commons";
import AlertManager from "../shared/Alert/Alert";
import { getTemplateFileXlsx } from "../templates/CostUpload/FileReceiver";

const normalizeDataRows = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.content)) {
    return payload.content;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload?.rows)) {
    return payload.rows;
  }
  return [];
};

const buildFormDataPayload = (payload = {}) => {
  const formData = new FormData();
  const appendValue = (key, value) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, value);
    }
  };

  appendValue("file", payload.file);
  appendValue("startDate", payload.startDate);
  appendValue("endDate", payload.endDate);
  appendValue("page", payload.page);
  appendValue("size", payload.size);

  return formData;
};

const buildExportFormDataPayload = (payload = {}) => {
  const formData = new FormData();
  const appendValue = (key, value) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, value);
    }
  };

  appendValue("file", payload.file);
  appendValue("startDate", payload.startDate);
  appendValue("endDate", payload.endDate);

  return formData;
};

const normalizePagedResponse = (payload, fallbackPage = 0, fallbackSize) => {
  const rows = normalizeDataRows(payload);

  if (Array.isArray(payload)) {
    const pageSize = fallbackSize ?? rows.length;
    return {
      content: rows,
      rows,
      pageNumber: fallbackPage,
      pageSize,
      totalElements: rows.length,
      totalPages: rows.length ? 1 : 0,
      hasNext: false,
      totalRows: rows.length,
      page: fallbackPage,
    };
  }

  const totalElements = Number(
    payload?.totalElements ?? payload?.totalRecords ?? payload?.totalRows ?? payload?.count
  );
  const totalPages = Number(payload?.totalPages ?? payload?.pages ?? payload?.pageCount);
  const pageNumber = Number(payload?.pageNumber ?? payload?.page ?? payload?.number ?? fallbackPage);
  const pageSize = Number(payload?.pageSize ?? payload?.size ?? fallbackSize ?? rows.length);
  const hasNext = Boolean(payload?.hasNext);

  return {
    content: rows,
    rows,
    pageNumber: Number.isFinite(pageNumber) ? pageNumber : fallbackPage,
    pageSize: Number.isFinite(pageSize) ? pageSize : rows.length,
    totalElements: Number.isFinite(totalElements) ? totalElements : rows.length,
    totalPages: Number.isFinite(totalPages) ? totalPages : rows.length ? 1 : 0,
    hasNext,
    totalRows: Number.isFinite(totalElements) ? totalElements : rows.length,
    page: Number.isFinite(pageNumber) ? pageNumber : fallbackPage,
  };
};

export const generateLandedCostDataView = async (payload) => {
  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return null;
  }

  const formData = payload instanceof FormData ? payload : buildFormDataPayload(payload);
  const apiUrl = `${localDatabaseUrls.baseURL}${localDatabaseUrls.serviceURL.landedCostDataViewGenerate}`;

  const response = await postAPI(apiUrl, formData, tokenInfo);
  if (!response) {
    AlertManager.error("Unable to request the data view.", { contactSupport: false });
    return null;
  }

  if (response.ok) {
    const payload = await response.json().catch(() => []);
    const fallbackPage = payload?.pageNumber ?? payload?.number ?? 0;
    const fallbackSize = payload?.pageSize ?? payload?.size;
    return normalizePagedResponse(payload, fallbackPage, fallbackSize);
  }

  const errorData = await response.json().catch(() => ({}));
  AlertManager.error(errorData?.message || "Failed to generate landed cost data.", { contactSupport: false });
  return null;
};

const resolveDownloadFileName = (contentDisposition, fallbackName) => {
  if (contentDisposition && contentDisposition.includes("filename=")) {
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match?.[1]) {
      return match[1].replace(/["']/g, "");
    }
  }
  return fallbackName;
};

export const exportLandedCostDataViewCsv = async (payload) => {
  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return null;
  }

  const formData = payload instanceof FormData ? payload : buildExportFormDataPayload(payload);
  const apiUrl = `${localDatabaseUrls.baseURL}${localDatabaseUrls.serviceURL.landedCostDataViewExportCsv}`;

  const response = await postAPI(apiUrl, formData, tokenInfo);
  if (!response) {
    AlertManager.error("Unable to export the data view.", { contactSupport: false });
    return null;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    AlertManager.error(errorData?.message || "Unable to export the data view.", { contactSupport: false });
    return null;
  }

  const contentDisposition = response.headers?.get?.("Content-Disposition") ?? response.headers?.get?.("content-disposition");
  const fileName = resolveDownloadFileName(contentDisposition, "landed-cost-data-view.csv");
  await getTemplateFileXlsx(response, fileName);
  return true;
};

export default generateLandedCostDataView;
